import {
  ClientDataType,
  DataInfoType,
  StorageDataType,
  TrackingDataType,
  VariationDataType,
} from './types';
import { DEFAULT_CLIENT_CONFIGURATION, KameleoonStorageKey } from './constants';
import { TargetingDataType } from 'src/targeting';
import { KameleoonData } from 'src/kameleoonData/types';
import { exhaustCheck } from 'src/kameleoonError';

interface IIntegrityChecker {
  isDataValid(data: StorageDataType): boolean;
  getDefaultData(data: StorageDataType): StorageDataType;
}

export class IntegrityChecker implements IIntegrityChecker {
  private key: KameleoonStorageKey;

  constructor(key: KameleoonStorageKey) {
    this.key = key;
  }

  public isDataValid(data: StorageDataType): boolean {
    switch (this.key) {
      case KameleoonStorageKey.ClientData:
        return this.checkClientDataIntegrity(data as ClientDataType);
      case KameleoonStorageKey.TargetingData:
        return this.checkTargetingDataIntegrity(data as TargetingDataType);
      case KameleoonStorageKey.DataInfo:
        return this.checkDataInfoIntegrity(data as DataInfoType);
      case KameleoonStorageKey.VariationData:
        return this.checkVariationDataIntegrity(data as VariationDataType);
      case KameleoonStorageKey.TrackingData:
        return this.checkTrackingDataIntegrity(data as TrackingDataType);
      default:
        return true;
    }
  }

  public getDefaultData(): StorageDataType {
    switch (this.key) {
      case KameleoonStorageKey.ClientData:
        return this.getDefaultClientData();
      case KameleoonStorageKey.DataInfo:
        return { unsentData: {}, nextVisitTime: 0, mappingIdentifiers: {} };
      case KameleoonStorageKey.TrackingData:
        return { scheduledVisitors: {} };
      case KameleoonStorageKey.TargetingData:
      case KameleoonStorageKey.VariationData:
      case KameleoonStorageKey.ConsentData:
        return {};
      case KameleoonStorageKey.VisitorCode:
        return '';
      case KameleoonStorageKey.KameleoonSimulationFFData:
        return '';
      case KameleoonStorageKey.ForcedExperimentVariation:
        return {};
      case KameleoonStorageKey.ForcedFeatureVariation:
        return {};
      default:
        exhaustCheck(this.key);
    }
  }

  private checkTrackingDataIntegrity(data: TrackingDataType): boolean {
    return this.hasFields(data, ['scheduledVisitors']);
  }

  private checkDataInfoIntegrity(data: DataInfoType): boolean {
    return this.hasFields(data, ['unsentData', 'nextVisitTime']);
  }

  private checkVariationDataIntegrity(data: VariationDataType): boolean {
    const variations = Object.values(data);

    if (!variations.length) {
      return true;
    }

    const hasInvalidFields = (obj: Record<string, unknown>) =>
      !this.hasFields(obj, ['status']) || this.hasFields(obj, ['isSent']);

    for (const variation of variations) {
      for (const value of Object.values(variation)) {
        if (hasInvalidFields(value)) {
          return false;
        }
      }
    }

    return true;
  }

  private checkClientDataIntegrity(data: ClientDataType): boolean {
    return this.hasFields(data, Object.keys(DEFAULT_CLIENT_CONFIGURATION));
  }

  private checkTargetingDataIntegrity(data: TargetingDataType): boolean {
    const visitorValues = Object.values(data);

    if (!visitorValues.length) {
      return true;
    }

    for (const visitorData of visitorValues) {
      // --- Note ---
      // If `visitorData` is a string, it means that it's a visitor reference
      if (typeof visitorData === 'string') {
        continue;
      }

      const hasInvalidFields = (obj: Record<string, unknown>) =>
        !this.hasFields(obj, ['status']) ||
        this.hasFields(obj, ['sent']) ||
        this.hasFields(obj, ['timestampList']);

      for (const [key, data] of Object.entries(visitorData)) {
        if (Array.isArray(data)) {
          return false;
        }

        switch (key) {
          case KameleoonData.PageView:
          case KameleoonData.Conversion:
          case KameleoonData.Personalization:
          case KameleoonData.TargetedSegment:
          case KameleoonData.CustomData: {
            const hasBadFields = Object.values(data).some((obj) =>
              hasInvalidFields(obj as Record<string, unknown>),
            );

            if (hasBadFields) {
              return false;
            }

            continue;
          }
          default: {
            if (hasInvalidFields(data as Record<string, unknown>)) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  private getDefaultClientData(): ClientDataType {
    return {
      ...DEFAULT_CLIENT_CONFIGURATION,
      lastUpdate: new Date().toString(),
    };
  }

  private hasFields(obj: Record<string, unknown>, fields: string[]): boolean {
    const keys = Object.keys(obj);

    if (!keys.length || !fields.length) {
      return false;
    }

    return fields.every((field) => keys.includes(field));
  }
}
